import {
    Alignment, Color, CurrencySettings, CurrencyUtil, DateUtil, DisplayType, DisplayValue, DOMUtil, ExtendedDateFormat,
    GeneralSettings, getApiMetadata, getRelativeDateString, getThemeColor, HorizontalAlignment,
    isRightAlignedDisplayType, JSUtil, LeftOrRightAlignment, ModelRow, Navigation, StringUtil, VerticalAlignment
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { toDate } from "date-fns-tz";
import { Component } from "../../base/Component";
import { ComponentListenerDefs } from "../../base/ComponentListenerDefs";
import { ComponentPropDefinition } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ListenerListDef } from "../../base/ListenerListDef";
import { QuickInfo } from "../../base/QuickInfoComponent";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { Image } from "../../components/image/Image";
import { ClickListener } from "../../events/ClickEvent";
import { DomEvent } from "../../events/DomEvent";
import { ImageProps } from "../image/ImageProps";
import { LabelPropDefinitions, LabelProps } from "./LabelProps";
import { LabelStyles } from "./LabelStyles";
import { ReadMoreType } from "./ReadMoreType";
import { DesignableObjectTempState } from "../../base/DesignableObjectTempState";

export class Label extends Component implements LabelProps {
    private _linkClickHandler: ClickListener;

    private _allowSelect: boolean;
    private _caption: string;
    private _collapseVerticallyWhenEmpty: boolean;
    private _explicitDisplayType: DisplayType;
    private _format: string;
    private _image: Image;
    private _imageName: ImageName;
    private _imageWidth: number;
    private _imageHeight: number;
    private _imageRotation: number;
    private _imageProps: Partial<ImageProps>;
    private _imageAlign: LeftOrRightAlignment;
    private _imageMarginLeft: number
    private _imageMarginRight: number
    private _imageColor: Color;
    private _link: string;
    private _linkHardRefresh: boolean;
    private _verticalAlign: VerticalAlignment;
    private _wrap: boolean;
    private _readMoreDiv: HTMLSpanElement;
    private _readMoreLabel: Label;
    private _readMoreType: ReadMoreType;
    private _preReadMoreMaxHeight: string | number;
    private _readMoreCallback: () => void;
    private _textNode: Text;
    private _nullDisplayValue: string;
    private beforeBusyState: DesignableObjectTempState;
    private _busy: boolean;
    private _busySpinnerAlignment: LeftOrRightAlignment
    private _busyWhenDataSourceBusy: boolean;
    private _scrollX: boolean;
    private _scrollY: boolean;

    constructor(props?: Partial<LabelProps> | string) {
        if (typeof props === "string")
            props = { caption: props };
        super("div", props);
        this._textNode = document.createTextNode("");
        this._element.appendChild(this._textNode);
        this.addClass("cmp-base");
        this.addClass(LabelStyles.base);
        this.setProps(props);
    }

    override setProps(props: Partial<LabelProps>) {
        super.setProps(props);
    }

    override displayData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        if (this.field != null) {
            const value = data?.get(this.field);
            if (value != null) {
                if (this.displayType === DisplayType.DATETIME && this.format === ExtendedDateFormat.RELATIVE) {
                    //https://github.com/marnusw/date-fns-tz#time-zone-helpers
                    const dateInClientTz = toDate(value + " " + GeneralSettings.get().server_tz_id);
                    this.caption = getRelativeDateString(dateInClientTz, { object: this, propertyToSet: "caption" });
                    this.tooltip = DateUtil.formatDateTime(dateInClientTz, DateUtil.getUserDateTimeFormat());
                }
                else if (this.displayType === DisplayType.STRING && this.format != null)
                    this.caption = DisplayValue.getFormattedDataString(this.format, data);
                else if (this.displayType === DisplayType.CURRENCY) {
                    this.caption = CurrencyUtil.formatCurrency(data.get(this.field))
                    if (CurrencySettings.getSingleton().colorNegatives() === true) {
                        if (parseFloat(CurrencyUtil.removeFormatting(this.caption)) < 0)
                            this.addClass(LabelStyles.negativeCurrency)
                        else
                            this.removeClass(LabelStyles.negativeCurrency)
                    }
                }
                else if (this.displayType === DisplayType.TEMPERATURE) {
                    this.caption = `${data.get(this.field)}\u00B0`
                }
                else {
                    const metadataField = this._boundField != null ? this._boundField : this.dataSource?.getMetadataFromCache()?.output[this.field];
                    const dbDisplay = metadataField?.getDisplayValue(value);
                    if (dbDisplay != null)
                        this.caption = dbDisplay;
                    else
                        this.caption = DisplayValue.getDisplayValue(value, this.displayType, this.format);
                }
            }
            else
                this.caption = null; //null out hard-coded caption since there was no value in the bound field
        }
        if (this._nullDisplayValue == "hide") {
            this.visible = !StringUtil.isEmptyString(this.caption);
        }
        else if (this.caption == null) {
            this.caption = this._nullDisplayValue;
        }
        super.displayData(data, allData, rowIndex);
    }

    _setDesigner(value) {
        this.setClassIncluded(LabelStyles.noSelect, true);
    }

    get caption(): string {
        return this._caption;
    }

    set caption(value: string) {
        if (this._designer == null || this.field == null) {
            const oldValue = this._caption;
            if (oldValue !== value) {
                this._caption = value;
                this._syncCaption(oldValue);
                this._setDefaultImageMargins();
            }
        }
    }

    private _syncCaption(oldValue: string) {
        const value = this._caption;
        if (this.displayType === DisplayType.LINK) {
            if (StringUtil.isEmptyString(this.link) && !StringUtil.isEmptyString(this.caption)) {
                this.link = this.caption;
            }
        }
        else if (this.displayType === DisplayType.EMAIL && !StringUtil.isEmptyString(this.caption)) {
            this.link = "mailto:" + this.caption;

        } else if (this.displayType === DisplayType.PHONE && !StringUtil.isEmptyString(this.caption)) {
            this.link = `tel:${this.caption}`;
            this.tooltip = this.tooltip ? this.tooltip : "Click to dial";
        }

        this._setTextNodeValue(value);
        this._evaluateReadMore();

        this._showDesignerBorderIfEmpty();
        this._matchIdToValue(oldValue, value);
    }

    private _setTextNodeValue(value: string) {
        if (!this.collapseVerticallyWhenEmpty && StringUtil.isEmptyString(value)) {
            this._element.style.display = "block"; // may need to consider blanking this back out when text is non-empty
            value = " ";
        }
        else if (value == null)
            value = "";
        this._textNode.textContent = value;
    }

    override getFocusTarget(): HTMLElement {
        return undefined;
    }

    get maxHeight(): string | number {
        return super.maxHeight;
    }

    set maxHeight(value: string | number) {
        if (value != null) {
            this._preReadMoreMaxHeight = value;
        }
        super.maxHeight = value;
        if (value != null && ((typeof value === "string" && DOMUtil.convertStyleAttrToNumber(value) !== 0) || value !== 0)) {
            this._element.style.alignItems = "flex-start";
        }
        else {
            this._element.style.alignItems = "center";
        }
        this._evaluateReadMore();
    }

    get readMoreType(): ReadMoreType {
        return this._readMoreType == null ? ReadMoreType.NONE : this._readMoreType;
    }

    set readMoreType(value: ReadMoreType) {
        if (value === this._readMoreType)
            return;
        this._readMoreType = value;
        this.addMountListener(() => this._evaluateReadMore());
    }

    private _determineReadMoreAction(): (() => void) {
        switch (this.readMoreType) {
            case ReadMoreType.INCREASE_HEIGHT:
                this._toggleReadMore();
                break;
            case ReadMoreType.SHOW_IN_DIALOG:
                this._showReadMoreInDialog();
                break;
            default:
                return null;
        }
    }

    private _toggleReadMore() {
        const curr = this._element.style.maxHeight;
        if (curr == null || curr === "")
            this.maxHeight = this._preReadMoreMaxHeight;
        else
            this.maxHeight = null;

        if (this._readMoreCallback != null)
            this._readMoreCallback();
    }

    get readMoreCallback(): () => void {
        return this._readMoreCallback;
    }

    set readMoreCallback(fn: () => void) {
        this._readMoreCallback = fn;
    }

    private _showReadMoreInDialog() {
        ReflectiveDialogs.showDialog(this.caption);
    }

    private _evaluateReadMore() {
        if (this.readMoreType === ReadMoreType.NONE)
            return;
        // NOTE: calling element.clientHeight and scrollHeight will cause the DOM to reflow,
        // so if this Label is mounted and this is called repeatedly, this could cause performance problems
        if (!StringUtil.isEmptyString(this.caption) && this._element.clientHeight < this._element.scrollHeight) {
            this._element.style.display = "block";
            this._readMoreDiv = document.createElement("span");
            this._readMoreDiv.style.position = "absolute";
            this._readMoreDiv.style.bottom = "0px";
            this._readMoreDiv.style.margin = "0px";
            this._readMoreDiv.style.right = "0px";
            this._readMoreDiv.style.width = "100%";
            this._readMoreDiv.style.backgroundColor = DOMUtil.getStyleValueFromElementOrAncestor("backgroundColor", this._element, getThemeColor("defaultBackground"));
            if (this._readMoreLabel == null)
                this._readMoreLabel = new Label({
                    fontSize: this.fontSize,
                    color: "primary",
                    onClick: (event) => this._determineReadMoreAction(),
                    margin: 0,
                    padding: 0,
                    align: HorizontalAlignment.CENTER
                });
            this._readMoreLabel.caption = "Read More";
            this._readMoreDiv.appendChild(this._readMoreLabel.element);
            this._element.appendChild(this._readMoreDiv);
        }
        else if (this._readMoreLabel != null) {
            if (this._readMoreDiv != null) {
                this._readMoreDiv.removeChild(this._readMoreLabel.element);
                this._element.removeChild(this._readMoreDiv);
                this._readMoreDiv = null;
            }
            this._readMoreLabel.caption = "Read Less";
            // this._element.parentElement.insertBefore(this._readMoreLabel.element, this._element.nextSibling);
            this._element.appendChild(this._readMoreLabel.element);
        }
    }

    public get collapseVerticallyWhenEmpty() {
        return this._collapseVerticallyWhenEmpty;
    }

    public set collapseVerticallyWhenEmpty(value: boolean) {
        this._collapseVerticallyWhenEmpty = value;
        this._syncCaption(this.caption);
    }

    get align(): HorizontalAlignment {
        if (super.align === undefined && isRightAlignedDisplayType(this.displayType))
            return HorizontalAlignment.RIGHT;
        return super.align || HorizontalAlignment.LEFT;
    }

    set align(value: HorizontalAlignment) {
        super.align = value;
        this._syncAlign();
    }

    get allowSelect() {
        return this._allowSelect == null ? true : this._allowSelect;
    }

    set allowSelect(value) {
        this._allowSelect = value;
        this.setClassIncluded(LabelStyles.noSelect, !value)
    }

    get verticalAlign(): VerticalAlignment {
        return this._verticalAlign;
    }

    set verticalAlign(value: VerticalAlignment) {
        this._verticalAlign = value;
        if (value === VerticalAlignment.TOP)
            this._element.style.alignSelf = "flex-start";
        else if (value === VerticalAlignment.BOTTOM)
            this._element.style.alignSelf = "flex-end";
        else
            this._element.style.alignSelf = "";
    }

    override get field(): string {
        return super.field;
    }

    override set field(value: string) {
        if (this._designer != null) {
            if (value != null && this.caption != null)
                this.caption = null;
            this._textNode.textContent = value;
        }
        super.field = value;
        this._showDesignerBorderIfEmpty();
    }

    get format(): string {
        return this._format;
    }

    set format(value: string) {
        this._format = value;
    }

    get displayType(): DisplayType {
        let result = this._explicitDisplayType;
        if (result === undefined && this._boundField != null)
            result = this._boundField.displayType;
        return result;
    }

    set displayType(value: DisplayType) {
        this._explicitDisplayType = value;

        if (this.displayType === DisplayType.LINK) {
            if (StringUtil.isEmptyString(this.caption) && !StringUtil.isEmptyString(this.link)) {
                this.caption = this.link;
            }
            if (StringUtil.isEmptyString(this.link) && !StringUtil.isEmptyString(this.caption)) {
                this.link = this.caption;
            }
        }
        if (this.displayType === DisplayType.EMAIL && !StringUtil.isEmptyString(this.caption)) {
            this.link = "mailto:" + this.caption;
        }
        if (this.displayType === DisplayType.PHONE && !StringUtil.isEmptyString(this.caption)) {
            this.link = `tel:${this.caption}`;
            this.tooltip = this.tooltip ? this.tooltip : "Click to dial";
        }
        this._syncAlign();
    }

    private _syncAlign() {
        const value = this.align;
        let styleValue = "";
        if (value === HorizontalAlignment.RIGHT)
            styleValue = "right";
        else if (value === HorizontalAlignment.CENTER)
            styleValue = "center";
        this.style.justifyContent = styleValue; // if display is flex, it will use this style
        this.style.textAlign = styleValue; // if display is block, it will use this style
    }

    protected override _fieldBindingChanged(): void {
        super._fieldBindingChanged();
        if (this._boundField?.lookupModel != null) {
            getApiMetadata(this._boundField?.lookupModel).then(() => {
                // if there are prop getters that rely on the lookup model, add code to make them sync up here
                this["_syncHoverCallback"]();
            });
        }
        this._syncAlign();
        this["_syncHoverCallback"]();
    }

    get link() {
        return this._link;
    }

    set link(value) {
        this._link = value;

        if (this.displayType === DisplayType.LINK) {
            if (StringUtil.isEmptyString(this.caption) && !StringUtil.isEmptyString(this.link)) {
                this.caption = this.link;
            }
        }

        if (this._linkClickHandler != null)
            this.removeClickListener(this._linkClickHandler);
        if (value != null) {
            this._linkClickHandler = (event) => this.handleLinkClick(event);
            this.addClickListener(this._linkClickHandler);
        }
        this.setClassIncluded(LabelStyles.link, value != null);
    }

    get linkHardRefresh(): boolean {
        return this._linkHardRefresh;
    }

    set linkHardRefresh(value: boolean) {
        this._linkHardRefresh = value;
    }

    handleLinkClick(event) {
        const row = this?.dataSource?.activeRow;
        const link = DisplayValue.getFormattedDataString(this.link, row);
        if (link.startsWith("mailto:") || link.startsWith("tel:")) {
            window.location.href = this.link;
        }
        else if (link.indexOf("://") >= 0)
            window.open(link, "_blank");
        else
            Navigation.navigateTo(link, { hardRefresh: this.linkHardRefresh });
    }

    get wrap() {
        return this._wrap == null ? true : this._wrap;
    }

    set wrap(value) {
        this._wrap = value;
        if (value) {
            this._element.style.wordBreak = "break-word";
            this._element.style.whiteSpace = "pre-wrap";
            this._element.style.display = "";
        }
        else {
            this._element.style.wordBreak = "";
            this._element.style.whiteSpace = "nowrap";
            this._element.style.display = "inline-block";
        }
    }

    get imageName(): ImageName {
        return this._imageName;
    }

    set imageName(value: ImageName) {
        this._imageName = value;
        this._imageUpdated();
    }

    get imageColor(): Color {
        return this._imageColor;
    }

    set imageColor(value: Color) {
        this._imageColor = value;
        this._imageUpdated();
    }

    get imageWidth(): number {
        return this._imageWidth;
    }

    set imageWidth(value: number) {
        this._imageWidth = value;
        this._imageUpdated();
    }

    get imageHeight(): number {
        return this._imageHeight;
    }

    set imageHeight(value: number) {
        this._imageHeight = value;
        this._imageUpdated();
    }

    get imageRotation(): number {
        return this._imageRotation;
    }

    set imageRotation(value: number) {
        this._imageRotation = value;
        this._imageUpdated();
    }

    get imageAlign() {
        return this._imageAlign;
    }

    set imageAlign(value: LeftOrRightAlignment) {
        if (value === this._imageAlign)
            return;
        this._imageAlign = value;
        this._imageUpdated();
        // this._adjustImageCaption(true);
    }

    get imageProps(): Partial<ImageProps> {
        return this._imageProps;
    }

    set imageProps(value: Partial<ImageProps>) {
        this._imageProps = value;
        this._imageUpdated();
    }

    get imageMarginRight(): number {
        return this._imageMarginRight;
    }

    set imageMarginRight(value: number) {
        this._imageMarginRight = value;
        this._imageUpdated();
    }

    get imageMarginLeft(): number {
        return this._imageMarginLeft;
    }

    set imageMarginLeft(value: number) {
        this._imageMarginLeft = value;
        this._imageUpdated();
    }

    get nullDisplayValue(): string {
        return this._nullDisplayValue;
    }

    set nullDisplayValue(value: string) {
        this._nullDisplayValue = value;
    }

    _imageUpdated() {
        if (this._imageName == null && this._imageProps?.name == null) {
            if (this._image != null && this._element.contains(this._image._element))
                this._element.removeChild(this._image._element);
            this._image = null;
        }
        else {
            const props = {
                name: this._imageName,
                width: this._imageWidth,
                height: this._imageHeight,
                color: this._imageColor,
                rotation: this._imageRotation,
                ...this.imageProps
            };
            if (this._image == null)
                this._image = ComponentTypes.createComponentOfType("image", props);
            else
                this._image.setProps(props);
            if (this._image._element.parentElement !== this._element) {
                if (this.imageAlign !== Alignment.RIGHT) //default to left-aligned
                    this._element.prepend(this._image._element);
                else
                    this._element.append(this._image._element);
            }
            this._setDefaultImageMargins();
        }
        this._showDesignerBorderIfEmpty();
    }

    private _setDefaultImageMargins() {
        if (this._image == null || this.caption == null)
            return;
        if (this._image.marginLeft == null)
            this._image.marginLeft = this._imageMarginLeft == null ? 4 : this._imageMarginLeft;
        if (this._image.marginRight == null)
            this._image.marginRight = this._imageMarginRight == null ? 4 : this._imageMarginRight;
    }

    _showDesignerBorderIfEmpty() {
        if (this._designer != null) {
            if (this.isEmpty() && StringUtil.isEmptyString(this._textNode.textContent))
                this._element.classList.add(LabelStyles.designerEmpty);
            else
                this._element.classList.remove(LabelStyles.designerEmpty);
        }
    }

    get busyWhenDataSourceBusy(): boolean {
        return this._busyWhenDataSourceBusy;
    }

    set busyWhenDataSourceBusy(value: boolean) {
        this._busyWhenDataSourceBusy = value;
    }

    get busy(): boolean {
        return this._busy == null ? false : this._busy;
    }

    set busy(value: boolean) {
        this._busy = value;
        if (value === true) {
            this.saveBeforeBusyState();
            this.displayBusySpinner();
        }
        else {
            this.setProps(this.beforeBusyState?.originalProps);
            this.beforeBusyState = null;
        }
        if (this._image != null)
            this._image.rotate = value;
    }

    private saveBeforeBusyState() {
        this.beforeBusyState = new DesignableObjectTempState({
            imageAlign: this._imageAlign,
            imageName: this._imageName,
            imageColor: this._imageColor,
            imageHeight: this._imageHeight,
            imageWidth: this._imageWidth,
            imageMarginLeft: this._imageMarginLeft,
            imageMarginRight: this._imageMarginRight,
            imageRotation: this._imageRotation,
            imageStroke: this._imageStroke,
            imageStrokeWidth: this._imageStrokeWidth,
            imageProps: this._imageProps
        });
    }

    private displayBusySpinner() {
        //fit the busy spinner image to the size of the label, so that the label doesn't change height when busy
        const thisHeightWidth = DOMUtil.getElementHeightWidth(this._element, true, true);
        this._imageMarginLeft = 4;
        this._imageMarginRight = 4;
        this._imageHeight = thisHeightWidth.height - 8;
        this._imageWidth = thisHeightWidth.height - 8;
        this._imageAlign = this.busySpinnerAlignment;
        this._imageStroke = null;
        this._imageStrokeWidth = null;
        this._imageColor = this.color;
        //calling proper setting of imageName only (and not other props above) so that we only call imageUpdated() once
        this.imageName = "spinner";
    }

    public get busySpinnerAlignment(): LeftOrRightAlignment {
        return this._busySpinnerAlignment || this.getPropertyDefinitions().busySpinnerAlignment.defaultValue;
    }

    public set busySpinnerAlignment(value: LeftOrRightAlignment) {
        this._busySpinnerAlignment = value;
    }

    protected _getDefaultEventProp(): string {
        return "onDataDisplay";
    }

    override getPropertyDefinitions() {
        return LabelPropDefinitions.getDefinitions();
    }

    override getPropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "displayType")
            return this._boundField?.displayType || DisplayType.STRING;
        else if (prop.name === "align") {
            if (isRightAlignedDisplayType(this.displayType))
                return HorizontalAlignment.RIGHT;
            else
                return HorizontalAlignment.LEFT;
        }
        else if (prop.name === "wrap")
            return true;
        else if (prop.name === "quickInfoLayout") {
            return this["getQuickInfoLayoutDefaultValue"]();
        }
        return super.getPropertyDefaultValue(prop);
    }

    protected override isPropSet(propName: string): boolean {
        if (propName === "allowSelect")
            return this._allowSelect != null;
        return super.isPropSet(propName);
    }

    override getSearchValues(): string[] {
        const result = [];
        result.push(this.caption);
        return result;
    }

    override get serializationName() {
        return "label";
    }

    override get properName(): string {
        return "Label";
    }

    override getBasicValue(): any {
        return this.caption;
    }

    get quickInfoLayout(): string {
        return this["_mixin-QuickInfo-quickInfoLayout"];
    }

    set quickInfoLayout(value: string) {
        this["_mixin-QuickInfo-quickInfoLayout"] = value;
    }

    override domEventFiringListeners(event: DomEvent, eventDef: ListenerListDef): boolean {
        if (eventDef === ComponentListenerDefs.click && this.busy)
            return false;
        return super.domEventFiringListeners(event, eventDef)
    }

    public override isEmpty(): boolean {
        return StringUtil.isEmptyString(this.caption) && this.imageName == null;
    }

    public get scrollX(): boolean {
        return this._scrollX;
    }

    public set scrollX(value: boolean) {
        this._scrollX = value;
        this.style.overflowX = value ? "auto" : "";
    }

    public get scrollY(): boolean {
        return this._scrollY;
    }

    public set scrollY(value: boolean) {
        this._scrollY = value;
        this.style.overflowY = value ? "auto" : "";
    }
}

JSUtil.applyMixins(Label, [QuickInfo]);
ComponentTypes.registerComponentType("label", Label.prototype.constructor);
